/**
 * SurveyFlowMap
 *
 * P_BRANCHING_TREE_AUTHORING_1.0 — visual + lint feedback for the
 * authoring experience. Renders an ASCII tree of the survey path and
 * surfaces linter findings:
 *   - Sibling-branch leaks (Q in branch X linear-advances into a Q in
 *     branch Y) + a one-click "Fix all" button that sets
 *     `next_step={type:'end'}` on every leaking question.
 *   - Unreachable questions
 *   - Branching choices missing jump targets
 *
 * Goal: let a non-technical author see at a glance whether their
 * survey is a clean decision tree, and recover from the most common
 * mistake (sibling-branch leak) in one click.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';

import { analyzeSurveyFlow, healSiblingBranchLeaks } from '../utils/surveyFlow';

const SurveyFlowMap = ({ questions, onHeal }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);

    const analysis = useMemo(() => analyzeSurveyFlow(questions || []), [questions]);
    const { leaks, unreachable, tree } = analysis;

    const handleHeal = () => {
        const result = healSiblingBranchLeaks(questions);
        if (result.changed && typeof onHeal === 'function') {
            onHeal(result.questions, result.fixedCount);
        }
    };

    const allGreen = leaks.length === 0 && unreachable.length === 0;

    return (
        <Card
            data-testid="survey-flow-map"
            data-insight-id="surveys.workflow.flow-map"
            data-insight-label="Survey path"
        >
            <CardContent className="pt-4 pb-4 space-y-3">
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="w-full flex items-center justify-between gap-2"
                    data-testid="survey-flow-map-toggle"
                >
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        {allGreen ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                        )}
                        <span>{t('surveys.flowMap.title', 'Survey path')}</span>
                        {!allGreen && (
                            <span className="text-xs text-amber-700 dark:text-amber-300 ml-1">
                                {t('surveys.flowMap.issuesFound', {
                                    leaks: leaks.length,
                                    unreachable: unreachable.length,
                                    defaultValue: '{{leaks}} leak(s), {{unreachable}} unreachable'
                                })}
                            </span>
                        )}
                    </div>
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {open && (
                    <div className="space-y-3">
                        {/* Linter warnings */}
                        {leaks.length > 0 && (
                            <div
                                className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3 space-y-2"
                                data-testid="flow-map-leaks-panel"
                            >
                                <div className="flex items-start gap-2 text-sm">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 space-y-1">
                                        <p className="font-medium text-amber-900 dark:text-amber-200">
                                            {t('surveys.flowMap.leakHeader', 'Sibling-branch leaks detected')}
                                        </p>
                                        <p className="text-xs text-amber-800 dark:text-amber-300">
                                            {t(
                                                'surveys.flowMap.leakHelp',
                                                'These questions belong to one branch but will spill into another branch. Each one should end the survey or jump to a common rejoin point.'
                                            )}
                                        </p>
                                        <ul className="list-disc list-inside text-xs text-amber-800 dark:text-amber-300 space-y-0.5">
                                            {leaks.map((l) => (
                                                <li key={l.qid} className="font-mono">
                                                    {l.qid} → {l.leakingIntoQid}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                    onClick={handleHeal}
                                    data-testid="flow-map-heal-btn"
                                >
                                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                    {t('surveys.flowMap.healButton', 'Fix all — End survey after each leaking question')}
                                </Button>
                            </div>
                        )}

                        {unreachable.length > 0 && (
                            <div
                                className="rounded-md border border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 p-3 text-xs"
                                data-testid="flow-map-unreachable-panel"
                            >
                                <p className="font-medium mb-1">
                                    {t('surveys.flowMap.unreachableHeader', 'Unreachable questions')}
                                </p>
                                <p className="text-slate-600 dark:text-slate-400">
                                    {t(
                                        'surveys.flowMap.unreachableHelp',
                                        'No path reaches these questions; respondents will never see them. Wire a jump into them or delete.'
                                    )}
                                </p>
                                <ul className="list-disc list-inside font-mono mt-1">
                                    {unreachable.map((qid) => (
                                        <li key={qid}>{qid}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* ASCII flow tree */}
                        <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                                {t('surveys.flowMap.treeHeader', 'Flow tree')}
                            </p>
                            <pre className="text-xs font-mono whitespace-pre overflow-x-auto text-slate-800 dark:text-slate-200" data-testid="flow-map-tree">
{tree || t('surveys.flowMap.empty', 'Add a question to start building the flow.')}
                            </pre>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SurveyFlowMap;
